import type { CatalogEntry, CatalogKind, Fermentable, Hop, Misc, Yeast } from '@beerwright/catalog';
import type { CustomIngredientDoc, RecipeDoc } from '@beerwright/commands';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  defaultSortColumn,
  IngredientsPage,
  type SourceFilter,
} from '@/components/ingredients/ingredients-page';
import {
  fermentableItemFromCustom,
  hopItemFromCustom,
  miscItemFromCustom,
  yeastItemFromCustom,
} from '@/lib/custom-ingredients';
import { newId } from '@/lib/ids';
import { parseSearchEnum, parseSearchFlag, parseSearchString } from '@/lib/search-params';
import type { StartingAmountContext } from '@/store/add-ingredient';
import { fermentableItemFor, hopItemFor, miscItemFor, yeastItemFor } from '@/store/add-ingredient';
import { calculationInWorkspace } from '@/store/calculations';
import { useRecipeActions } from '@/store/recipe-actions';
import { useDispatch } from '@/store/use-dispatch';
import { selectUnits, useWorkspaceStore } from '@/store/workspace-context';

/** Every tab this page has — the allowed set for `?kind=`. */
const CATALOG_KINDS = [
  'fermentable',
  'hop',
  'yeast',
  'misc',
  'style',
] as const satisfies readonly CatalogKind[];

/** The allowed set for `?source=`. `all` is the default and never appears — see `IngredientsSearch.source`. */
const SOURCE_FILTERS = ['catalog', 'custom'] as const satisfies readonly SourceFilter[];

/** The allowed set for `?dir=`. */
const SORT_DIRECTIONS = ['asc', 'desc'] as const;

/** What `/ingredients` reads out of its own URL. */
export interface IngredientsSearch {
  /**
   * A catalog id to open pre-selected — the deep link the generated
   * ingredient pages print as "Open in the studio"
   * (`https://app.beerwright.com/ingredients?id=<catalog id>`).
   *
   * `undefined`, not `null`, for "absent": TanStack Router's default search
   * serializer stringifies every key `validateSearch` returns, `null`
   * included, so a plain visit with no `id` at all was round-tripping to
   * `/ingredients?id=null` the moment this page wrote its own URL back (a
   * navigation, a copied link). `undefined` is the one value `encode` drops.
   */
  readonly id: string | undefined;
  /**
   * Which tab is open. `undefined` for the default tab (`fermentable`) —
   * see `IngredientsPageProps.kind` for how this interacts with `id`.
   */
  readonly kind: CatalogKind | undefined;
  /** The search box. */
  readonly q: string | undefined;
  /**
   * The sort column. A pair with `dir`, spelled as two independent params
   * rather than one combined value (`?sort=alpha-desc`) because that is what
   * `search-params.ts`'s existing parsers already compose without a new one:
   * `sort` is free-form text (`parseSearchString`), since which column ids
   * are valid depends on the open tab, and a column that does not apply to
   * it already degrades safely — the table's own sort falls back to the
   * tab's first column rather than rendering nothing (the same guard a tab
   * switch relies on). `undefined` when it matches the open tab's own
   * default column (`defaultSortColumn`), so a bare `/ingredients` and every
   * tab's own "no sort chosen yet" state both write nothing.
   */
  readonly sort: string | undefined;
  /**
   * The sort direction. `undefined` for ascending — the default direction
   * for every column, on every tab — so only "descending" is ever worth a
   * character in the address bar.
   */
  readonly dir: 'asc' | 'desc' | undefined;
  /**
   * The "All" / "Built-in" / "Yours" filter. `undefined` for `all` — the
   * one member of `SourceFilter` `validateSearch` never produces, since it
   * is the default and so never worth a character in the URL.
   */
  readonly source: Exclude<SourceFilter, 'all'> | undefined;
  /**
   * The "In stock" filter (PLAN-UX-PASS F6). `true` when on, `undefined` when
   * off — the default, and so not worth a character in the address bar, which
   * is `parseSearchFlag`'s whole contract.
   */
  readonly stocked: true | undefined;
}

/**
 * `?id=` as a plain string, or `undefined`.
 *
 * The 2,897 generated pages that link here are static and outlive the catalog
 * release they were built from: a variety gets renamed, retired, or was never
 * spelled the way a hand-edited link remembers it. None of that is this
 * function's problem — it only rejects shapes that cannot be an id at all
 * (missing, or the array a repeated `?id=` would produce). Whether the id
 * still *names* something is a catalog lookup, and belongs to the page, which
 * already knows how to fail that check quietly and land on the ordinary view.
 */
function parseCatalogId(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

export const Route = createFileRoute('/ingredients')({
  validateSearch: (
    search: {
      readonly id?: unknown;
      readonly kind?: unknown;
      readonly q?: unknown;
      readonly sort?: unknown;
      readonly dir?: unknown;
      readonly source?: unknown;
      readonly stocked?: unknown;
    } & SearchSchemaInput,
  ): IngredientsSearch => ({
    id: parseCatalogId(search.id),
    kind: parseSearchEnum(search.kind, CATALOG_KINDS),
    q: parseSearchString(search.q),
    sort: parseSearchString(search.sort),
    dir: parseSearchEnum(search.dir, SORT_DIRECTIONS),
    source: parseSearchEnum(search.source, SOURCE_FILTERS),
    stocked: parseSearchFlag(search.stocked),
  }),
  component: IngredientsRoute,
});

function IngredientsRoute() {
  const { id, kind, q, sort, dir, source, stocked } = Route.useSearch();
  // Scoped `from`, not the bare hook: an unscoped `useNavigate` types `search`
  // against every route's schema at once (registered app-wide), which cannot
  // know this route's own `kind`/`q`/`sort`/`dir`/`source` shape and rejects
  // the updaters below.
  const navigate = useNavigate({ from: Route.fullPath });
  const { createRecipe } = useRecipeActions();
  const { dispatch } = useDispatch();
  const store = useWorkspaceStore();

  /**
   * What the add helpers size a row against.
   *
   * Built from the recipe that `createRecipe` just made, not from anything held
   * across the click: the document is one command old, and its calculation —
   * empty grist, default equipment, the batch size the workspace defaults to —
   * is what says how much of this malt lands the beer on 1.050.
   */
  function startingContext(doc: RecipeDoc): StartingAmountContext {
    const state = store.getState();
    return {
      doc,
      calculation: calculationInWorkspace(state, doc),
      units: selectUnits(state),
      massFormat: state.settings.massFormat,
    };
  }

  /**
   * "Start a recipe with this" — create a recipe named after the entry, then
   * seed it with that one ingredient (or, for a style, with the style itself).
   *
   * Two commands rather than one: `recipe.create` takes a whole template, but
   * the add helpers need the created document to decide a sensible amount (a
   * first grain is a base malt, a first hop is a 60 minute charge). Two undo
   * steps is the honest cost, and the second is immediately visible.
   */
  function startRecipe(kind: CatalogKind, entry: CatalogEntry): string | null {
    const recipeId = createRecipe(`${entry.name} — new recipe`.slice(0, 120));
    if (recipeId === null) return null;
    const doc = store.getState().recipes[recipeId];
    if (doc === undefined) return recipeId;
    const context = startingContext(doc);

    switch (kind) {
      case 'fermentable':
        dispatch('recipe.addFermentable', {
          recipeId,
          item: fermentableItemFor(entry as Fermentable, context, newId('f')),
        });
        break;
      case 'hop':
        dispatch('recipe.addHop', {
          recipeId,
          item: hopItemFor(entry as Hop, context, newId('h')),
        });
        break;
      case 'yeast':
        dispatch('recipe.addYeast', {
          recipeId,
          item: yeastItemFor(entry as Yeast, newId('y')),
        });
        break;
      case 'misc':
        dispatch('recipe.addMisc', {
          recipeId,
          item: miscItemFor(entry as Misc, doc, newId('m')),
        });
        break;
      case 'style':
        dispatch('recipe.setStyle', { recipeId, styleId: entry.id });
        break;
    }
    return recipeId;
  }

  function startCustom(item: CustomIngredientDoc): string | null {
    const recipeId = createRecipe(`${item.name} — new recipe`.slice(0, 120));
    if (recipeId === null) return null;
    const doc = store.getState().recipes[recipeId];
    if (doc === undefined) return recipeId;
    const context = startingContext(doc);

    switch (item.kind) {
      case 'fermentable':
        dispatch('recipe.addFermentable', {
          recipeId,
          item: fermentableItemFromCustom(item, context, newId('f')),
        });
        break;
      case 'hop':
        dispatch('recipe.addHop', {
          recipeId,
          item: hopItemFromCustom(item, context, newId('h')),
        });
        break;
      case 'yeast':
        dispatch('recipe.addYeast', {
          recipeId,
          item: yeastItemFromCustom(item, newId('y')),
        });
        break;
      case 'misc':
        dispatch('recipe.addMisc', {
          recipeId,
          item: miscItemFromCustom(item, newId('m')),
        });
        break;
    }
    return recipeId;
  }

  return (
    <IngredientsPage
      // The search *is* the view: `IngredientsPage` reads these on every
      // render rather than seeding from them, so a Back that only changes the
      // search params — which does not remount this route — moves the tab,
      // the sort and the filters with it.
      //
      // `catalogId` still reads `null` as "no id": `exactOptionalPropertyTypes`
      // treats an optional `string | null` prop and an explicit `undefined` as
      // different things, so the search's own `undefined` (chosen so the URL
      // drops `id` entirely; see `IngredientsSearch`) is translated back at
      // the one place that needs it.
      catalogId={id ?? null}
      kind={kind}
      query={q}
      sortColumn={sort}
      sortDirection={dir}
      source={source ?? 'all'}
      inStockOnly={stocked ?? false}
      onStartRecipe={startRecipe}
      onStartCustom={startCustom}
      onOpenRecipe={(recipeId) => {
        void navigate({ to: '/recipes/$recipeId', params: { recipeId } });
      }}
      // One navigation for one gesture, even where that gesture moves five
      // fields — see `IngredientsPageProps.onViewChange`. Every field is
      // canonicalised against the *kind being moved to*, not the previous
      // URL, so this one handler is correct whether the gesture reset those
      // fields (a direct tab click) or left them exactly as they were
      // (following a related entry to another tab).
      //
      // `id` comes from the event rather than from `prev`, and that is the
      // point: a tab click sends `null`, so the entry belonging to the tab
      // being left stops describing a view that is no longer on screen — and
      // stops out-ranking `?kind=` when the link is reopened. Following a
      // related entry sends the id it moved to, for the same reason.
      onViewChange={(next) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            id: next.id ?? undefined,
            kind: next.kind === 'fermentable' ? undefined : next.kind,
            q: next.query === '' ? undefined : next.query,
            sort:
              next.sort.columnId === defaultSortColumn(next.kind) ? undefined : next.sort.columnId,
            dir: next.sort.direction === 'asc' ? undefined : next.sort.direction,
            source: next.source === 'all' ? undefined : next.source,
          }),
        });
      }}
      // `replace: true`: a keystroke narrows the same search, it does not
      // start a new one — see `/recipes` for the identical reasoning.
      onQueryChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, q: next === '' ? undefined : next }),
          replace: true,
        });
      }}
      // A column header click is a decision, not a keystroke: it pushes.
      // Canonicalised against the *open* tab's own default column
      // (`prev.kind`, or the implicit `fermentable` when absent) — every
      // non-Styles tab shares the same default, so `prev.kind` disagreeing
      // with the tab actually on screen (only possible via a hand-edited
      // URL that also mismatches `id`) cannot misfire this in practice.
      onSortChange={(next) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            sort:
              next.columnId === defaultSortColumn(prev.kind ?? 'fermentable')
                ? undefined
                : next.columnId,
            dir: next.direction === 'asc' ? undefined : next.direction,
          }),
        });
      }}
      onSourceChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, source: next === 'all' ? undefined : next }),
        });
      }}
      // A filter choice, so it pushes, like the source filter beside it. Off is
      // `undefined` rather than `false`, so switching it back off leaves the URL
      // as clean as it was before (PLAN-UX-PASS F6).
      onInStockChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, stocked: next ? true : undefined }),
        });
      }}
    />
  );
}
